import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { API_BASE } from "../../app/const"
import { RootState } from "../../app/store"
import { fireauth } from "../auth/AuthSlice"

export interface InviteState {
  status: 'idle' | 'loading' | 'failed'
  invitUsers: string[]
  invitedUsers: any[]
  addededUsers: any[]
  faultedUsers: any[]
  inviteDetails: any
  inviteFault?: String
}

const initialState: InviteState = {
  status: 'idle',
  invitUsers: [],
  invitedUsers: [],
  addededUsers: [],
  faultedUsers: [],
  inviteDetails: {}
}

export const getInviteDetailsAsync = createAsyncThunk('invite/getInviteDetailsAsync', async ({ slug }: { slug: string }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.get(`${API_BASE}app/invite/${slug}`, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8"
      }
    })
    return response.data
  }
})

interface inviteUsersRsp {
  data: any[],
  fault: String
}
export const inviteUsersAsync = createAsyncThunk<inviteUsersRsp, { emails: string[], oid: string }>('invite/inviteUsersAsync', async ({ emails, oid }: { emails: string[], oid: string }, { getState }) => {
  const state = getState() as RootState
  if (state) {
    const response = await axios.post(`${API_BASE}apps/invite`, { emails }, {
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
      }
    })
    return {
      data: response.data || [],
      fault: response.headers['fault'] || ''
    }
  }
  return { data: [], fault: '' }
})

export const inviteSlice = createSlice({
  name: 'invite',
  initialState,
  reducers: {
    resetInvite: (state) => {
      state.status = 'idle'
      state.invitUsers = initialState.invitUsers
      state.invitedUsers = initialState.invitedUsers
    },
    clearInvitedUsers: (state) => {
      state.invitedUsers = initialState.invitedUsers
      state.addededUsers = initialState.addededUsers
      state.faultedUsers = initialState.faultedUsers
      state.status = 'idle'
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(inviteUsersAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(inviteUsersAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.invitedUsers = action.payload.data.filter((user: any) => !user.userId)
        state.addededUsers = action.payload.data.filter((user: any) => user.userId && user.org && user.org.id == action.meta.arg.oid)
        state.faultedUsers = action.payload.data.filter((user: any) => user.userId && user.org && user.org.id != action.meta.arg.oid)
        state.inviteFault = action.payload.fault
      }).addCase(inviteUsersAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.invitedUsers = initialState.invitedUsers
        state.faultedUsers = initialState.faultedUsers
        state.addededUsers = initialState.addededUsers
      }).addCase(getInviteDetailsAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(getInviteDetailsAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.inviteFault = initialState.inviteFault
        state.inviteDetails = action.payload
      }).addCase(getInviteDetailsAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.inviteDetails = initialState.inviteDetails
        state.inviteFault = initialState.inviteFault
      })
  }
})

export const { resetInvite, clearInvitedUsers } = inviteSlice.actions
export const selectInviteStatus = (state: RootState) => state.invite.status
export const selectInviteUsers = (state: RootState) => state.invite.invitedUsers
export const selectAddededUsers = (state: RootState) => state.invite.addededUsers
export const selectFaultedUsers = (state: RootState) => state.invite.faultedUsers
export const selectInviteDetails = (state: RootState) => state.invite.inviteDetails
export const selectInviteFault = (state: RootState) => state.invite.inviteFault
export default inviteSlice.reducer